<template>
  <div class="staff-management-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <div class="page-title">
            <div class="title-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <h1>Staff Management</h1>
          </div>
        </div>
        <div class="header-actions">
          <button class="btn btn-primary" @click="openAddStaffDrawer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Add Staff</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Tabs Section -->
    <div class="tabs-section">
      <div class="tabs">
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'staff' }"
          @click="activeTab = 'staff'"
        >
          Staff
        </button>
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'payment' }"
          @click="activeTab = 'payment'"
        >
          Staff Payment
        </button>
      </div>
    </div>

    <!-- Staff Tab Content -->
    <div v-if="activeTab === 'staff'" class="staff-content">
      <!-- Search Bar -->
      <div class="search-section">
        <div class="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input 
            type="text" 
            placeholder="Search" 
            v-model="searchQuery"
            @input="handleSearch"
          />
        </div>
      </div>

      <!-- Table Section -->
      <div class="table-section">
        <div class="table-container">
          <div class="table-loading-overlay" v-if="isLoading">
            <div class="loading-content">
              <div class="spinner"></div>
              <p class="loading-text">Loading staff...</p>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!isLoading && filteredStaff.length === 0" class="empty-state">
            <div class="empty-illustration">
              <img src="@/assets/guest-maint-staff.svg" alt="No staff illustration" class="empty-illustration-img" />
            </div>
            <p class="empty-text">No Data To Display</p>
          </div>

          <!-- Staff Table -->
          <div v-else class="table-wrapper">
            <table class="staff-table">
              <thead v-if="filteredStaff.length > 0 && !isLoading">
                <tr>
                  <th style="text-align: left;">Staff Name</th>
                  <th style="text-align: left;">Staff Role</th>
                  <th style="text-align: left;">Staff Phone Number</th>
                  <th style="text-align: left;">Address</th>
                  <th style="text-align: left;">Staff Email</th>
                  <th style="text-align: right;">Staff Salary</th>
                  <th style="text-align: left;">Added Date</th>
                  <th style="text-align: center;">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="staff in paginatedStaff" :key="staff.staffId" class="table-row">
                  <td>
                    <div class="staff-name-cell">
                      <div class="staff-avatar">
                        <img 
                          v-if="staff.image" 
                          :src="staff.image" 
                          :alt="`${staff.firstName} ${staff.lastName}`"
                          @error="handleImageError"
                        />
                        <div v-else class="avatar-placeholder">
                          {{ getInitials(staff.firstName, staff.lastName) }}
                        </div>
                      </div>
                      <span class="staff-name">{{ `${staff.firstName} ${staff.lastName}` }}</span>
                    </div>
                  </td>
                  <td style="text-align: left;">{{ staff.role || '--' }}</td>
                  <td style="text-align: left;">{{ staff.mobileno || '--' }}</td>
                  <td style="text-align: left;">{{ staff.address || '--' }}</td>
                  <td style="text-align: left;">{{ staff.email || '--' }}</td>
                  <td style="text-align: right;">{{ formatSalary(staff.salary) }}</td>
                  <td style="text-align: left;">{{ formatDate(staff.createdat) }}</td>
                  <td style="text-align: center;">
                    <div class="action-menu">
                      <button 
                        class="action-button"
                        @click="openEditStaffDrawer(staff)"
                        :title="`Edit ${staff.firstName} ${staff.lastName}`"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button 
                        class="action-button action-button-delete"
                        @click="confirmDeleteStaff(staff)"
                        :title="`Delete ${staff.firstName} ${staff.lastName}`"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredStaff.length > 0" class="pagination">
            <button 
              class="pagination-button"
              :disabled="currentPage === 1"
              @click="currentPage--"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <div class="pagination-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="pagination-number"
                :class="{ active: page === currentPage }"
                @click="currentPage = page"
              >
                {{ page }}
              </button>
              <span v-if="hasMorePages" class="pagination-ellipsis">...</span>
            </div>
            <button 
              class="pagination-button"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Staff Payment Tab Content -->
    <div v-if="activeTab === 'payment'" class="payment-content">
      <div class="table-section">
        <div class="table-container">
          <div class="table-loading-overlay" v-if="isPaymentLoading">
            <div class="loading-content">
              <div class="spinner"></div>
              <p class="loading-text">Loading payments...</p>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!isPaymentLoading && paymentList.length === 0" class="empty-state">
            <div class="empty-illustration">
              <img src="@/assets/guest-maint-staff.svg" alt="No payment data illustration" class="empty-illustration-img" />
            </div>
            <p class="empty-text">No Payment Data To Display</p>
          </div>

          <!-- Payment Table -->
          <div v-else class="table-wrapper">
            <table class="staff-table">
              <thead v-if="paymentList.length > 0 && !isPaymentLoading">
                <tr>
                  <th>Staff ID</th>
                  <th>Month/Year</th>
                  <th>Nepali Month/Year</th>
                  <th style="text-align: center;">Worked Days</th>
                  <th style="text-align: center;">Total Days</th>
                  <th style="text-align: right;">Basic Salary</th>
                  <th style="text-align: right;">Bonus</th>
                  <th style="text-align: right;">Deduction</th>
                  <th style="text-align: right;">Net Amount</th>
                  <th>Currency</th>
                  <th>Payment Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment in paymentList" :key="payment.salaryPaymentId" class="table-row">
                  <td>{{ payment.staffId || '--' }}</td>
                  <td>{{ payment.globalMonth }} {{ payment.globalYear }}</td>
                  <td>{{ payment.nepaliMonth }} {{ payment.nepaliYear }}</td>
                  <td style="text-align: center;">{{ payment.globalWorkedDays || '--' }}</td>
                  <td style="text-align: center;">{{ payment.globalTotalDays || '--' }}</td>
                  <td style="text-align: right;">{{ formatSalary(payment.basicSalary) }}</td>
                  <td style="text-align: right;">{{ formatSalary(payment.bonus) }}</td>
                  <td style="text-align: right;">{{ formatSalary(payment.deduction) }}</td>
                  <td class="net-amount" style="text-align: right;">{{ formatSalary(payment.netAmount) }}</td>
                  <td>{{ payment.currency || 'NPR' }}</td>
                  <td>{{ payment.paymentDate ? formatDate(payment.paymentDate) : '--' }}</td>
                  <td>
                    <span :class="['status-badge', payment.status ? 'status-active' : 'status-pending']">
                      {{ payment.status || 'Pending' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Staff Modal -->
    <div v-if="showAddDrawer" class="modal-overlay" @click.self="closeAddStaffDrawer">
      <div class="modal-content create-modal" @click.stop>
        <div class="modal-header">
          <div class="header-background"></div>
          <div class="modal-header-content">
            <h2>Add Staff</h2>
          </div>
          <button class="close-btn" @click="closeAddStaffDrawer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createStaff" class="create-staff-form">
          <!-- Profile Picture -->
          <div class="profile-picture-section">
            <div class="profile-picture-wrapper">
              <div v-if="!addForm.imagePreview" class="profile-placeholder">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <img v-else :src="addForm.imagePreview" alt="Profile" class="profile-image" />
              <div v-if="isUploadingProfile" class="upload-progress-overlay">
                <div class="spinner-container">
                  <div class="modern-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                  </div>
                  <p class="uploading-text">Uploading...</p>
                </div>
              </div>
            </div>
            <label class="change-photo-link">
              <input 
                type="file" 
                accept="image/*" 
                @change="handleProfileImageChange"
                style="display: none;"
                ref="profileImageInput"
              />
              <span v-if="!addForm.image">+ Add Profile Picture</span>
              <span v-else>Change Profile Photo</span>
            </label>
          </div>

          <!-- Form Fields -->
          <div class="form-fields">
            <div class="form-group">
              <label>First Name</label>
              <input 
                type="text" 
                v-model="addForm.firstName"
                placeholder="Enter First Name"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Last Name</label>
              <input 
                type="text" 
                v-model="addForm.lastName"
                placeholder="Enter Last Name"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Role</label>
              <input 
                type="text" 
                v-model="addForm.role"
                placeholder="Enter Staff Role"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Contact Number</label>
              <input 
                type="tel" 
                v-model="addForm.mobileno"
                placeholder="Enter Phone Number"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Address</label>
              <input 
                type="text" 
                v-model="addForm.address"
                placeholder="Enter Address"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Email (Optional)</label>
              <input 
                type="email" 
                v-model="addForm.email"
                placeholder="Enter Email"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Salary Per Month</label>
              <input 
                type="text" 
                v-model="addForm.salary"
                placeholder="Enter Amount"
                class="form-input"
              />
            </div>

            <!-- ID Proof Uploads -->
            <div class="form-group">
              <label>ID Proof Front</label>
              <div class="upload-box" @click="triggerIdProofFrontUpload">
                <input 
                  type="file" 
                  accept="image/*" 
                  @change="handleIdProofFrontChange"
                  style="display: none;"
                  ref="idProofFrontInput"
                />
                <div v-if="!addForm.idProofFrontPreview" class="upload-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#5b2e8e" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Upload Image</span>
                </div>
                <div v-else class="upload-preview">
                  <img :src="addForm.idProofFrontPreview" alt="ID Proof Front" />
                  <button 
                    class="remove-upload" 
                    @click.stop="removeIdProofFront"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div v-if="isUploadingIdFront" class="upload-progress-overlay">
                    <div class="spinner-container">
                      <div class="modern-spinner">
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                      </div>
                      <p class="uploading-text">Uploading...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>ID Proof Back</label>
              <div class="upload-box" @click="triggerIdProofBackUpload">
                <input 
                  type="file" 
                  accept="image/*" 
                  @change="handleIdProofBackChange"
                  style="display: none;"
                  ref="idProofBackInput"
                />
                <div v-if="!addForm.idProofBackPreview" class="upload-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#5b2e8e" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Upload Image</span>
                </div>
                <div v-else class="upload-preview">
                  <img :src="addForm.idProofBackPreview" alt="ID Proof Back" />
                  <button 
                    class="remove-upload" 
                    @click.stop="removeIdProofBack"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div v-if="isUploadingIdBack" class="upload-progress-overlay">
                    <div class="spinner-container">
                      <div class="modern-spinner">
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                      </div>
                      <p class="uploading-text">Uploading...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          </form>
        </div>

        <!-- Form Actions - Sticky -->
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" @click="closeAddStaffDrawer" :disabled="isSubmitting">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="createStaff" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Add Staff</span>
            <span v-else>Adding...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Staff Modal -->
    <div v-if="showEditDrawer" class="modal-overlay" @click.self="closeEditStaffDrawer">
      <div class="modal-content create-modal" @click.stop>
        <div class="modal-header">
          <div class="header-background"></div>
          <div class="modal-header-content">
            <h2>Edit Staff</h2>
          </div>
          <button class="close-btn" @click="closeEditStaffDrawer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateStaff" class="create-staff-form">
          <!-- Profile Picture -->
          <div class="profile-picture-section">
            <div class="profile-picture-wrapper">
              <img 
                v-if="editForm.imagePreview || editForm.image" 
                :src="editForm.imagePreview || editForm.image" 
                alt="Profile" 
                class="profile-image" 
              />
              <div v-else class="profile-placeholder">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div v-if="isUploadingProfile" class="upload-progress-overlay">
                <div class="spinner-container">
                  <div class="modern-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                  </div>
                  <p class="uploading-text">Uploading...</p>
                </div>
              </div>
            </div>
            <label class="change-photo-link">
              <input 
                type="file" 
                accept="image/*" 
                @change="handleEditProfileImageChange"
                style="display: none;"
                ref="editProfileImageInput"
              />
              Change Profile Photo
            </label>
          </div>

          <!-- Form Fields -->
          <div class="form-fields">
            <div class="form-group">
              <label>First Name</label>
              <input 
                type="text" 
                v-model="editForm.firstName"
                placeholder="Enter First Name"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Last Name</label>
              <input 
                type="text" 
                v-model="editForm.lastName"
                placeholder="Enter Last Name"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Role</label>
              <input 
                type="text" 
                v-model="editForm.role"
                placeholder="Enter Staff Role"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Contact Number</label>
              <input 
                type="tel" 
                v-model="editForm.mobileno"
                placeholder="Enter Phone Number"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Address</label>
              <input 
                type="text" 
                v-model="editForm.address"
                placeholder="Enter Address"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Email (Optional)</label>
              <input 
                type="email" 
                v-model="editForm.email"
                placeholder="Enter Email"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>Staff Salary Per Month</label>
              <input 
                type="text" 
                v-model="editForm.salary"
                placeholder="Enter Amount"
                class="form-input"
              />
            </div>

            <!-- ID Proof Uploads -->
            <div class="form-group">
              <label>ID Proof Front</label>
              <div class="upload-box" @click="triggerEditIdProofFrontUpload">
                <input 
                  type="file" 
                  accept="image/*" 
                  @change="handleEditIdProofFrontChange"
                  style="display: none;"
                  ref="editIdProofFrontInput"
                />
                <div v-if="!editForm.idProofFrontPreview" class="upload-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#5b2e8e" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Upload Image</span>
                </div>
                <div v-else class="upload-preview">
                  <img :src="editForm.idProofFrontPreview" alt="ID Proof Front" />
                  <button 
                    class="remove-upload" 
                    @click.stop="removeEditIdProofFront"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div v-if="isUploadingEditIdFront" class="upload-progress-overlay">
                    <div class="spinner-container">
                      <div class="modern-spinner">
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                      </div>
                      <p class="uploading-text">Uploading...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>ID Proof Back</label>
              <div class="upload-box" @click="triggerEditIdProofBackUpload">
                <input 
                  type="file" 
                  accept="image/*" 
                  @change="handleEditIdProofBackChange"
                  style="display: none;"
                  ref="editIdProofBackInput"
                />
                <div v-if="!editForm.idProofBackPreview" class="upload-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#5b2e8e" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Upload Image</span>
                </div>
                <div v-else class="upload-preview">
                  <img :src="editForm.idProofBackPreview" alt="ID Proof Back" />
                  <button 
                    class="remove-upload" 
                    @click.stop="removeEditIdProofBack"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div v-if="isUploadingEditIdBack" class="upload-progress-overlay">
                    <div class="spinner-container">
                      <div class="modern-spinner">
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                      </div>
                      <p class="uploading-text">Uploading...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          </form>
        </div>

        <!-- Form Actions - Sticky -->
        <div class="form-actions">
          <button 
            type="button"
            class="btn btn-secondary" 
            @click="closeEditStaffDrawer"
            :disabled="isSubmitting"
          >
            Cancel
          </button>
          <button 
            type="button"
            class="btn btn-primary" 
            @click="updateStaff"
            :disabled="isSubmitting"
          >
            <span v-if="!isSubmitting">Update Staff</span>
            <span v-else>Updating...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="closeDeleteConfirm">
      <div class="modal-content delete-confirm-modal" @click.stop>
        <div class="modal-header">
          <div class="header-background"></div>
          <div class="modal-header-content">
            <h2>Delete Staff</h2>
          </div>
          <button class="close-btn" @click="closeDeleteConfirm">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-confirm-content">
            <div class="delete-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h3>Are you sure you want to delete this staff member?</h3>
            <p v-if="staffToDelete" class="delete-staff-name">
              {{ staffToDelete.firstName }} {{ staffToDelete.lastName }}
            </p>
            <p class="delete-warning">
              This action cannot be undone. All staff data will be permanently deleted.
            </p>
          </div>
        </div>
        <div class="form-actions">
          <button 
            type="button"
            class="btn btn-secondary" 
            @click="closeDeleteConfirm"
            :disabled="isDeleting"
          >
            Cancel
          </button>
          <button 
            type="button"
            class="btn btn-danger" 
            @click="deleteStaff"
            :disabled="isDeleting"
          >
            <span v-if="!isDeleting">Delete Staff</span>
            <span v-else>Deleting...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';

export default {
  name: 'StaffManagement',
  props: {
    objectData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTab: 'staff',
      searchQuery: '',
      staffList: [],
      isLoading: false,
      isPaymentLoading: false,
      paymentList: [],
      currentPage: 1,
      pageSize: 10,
      showAddDrawer: false,
      showEditDrawer: false,
      isSubmitting: false,
      isDeleting: false,
      editingStaff: null,
      
      // Add Form
      addForm: {
        firstName: '',
        lastName: '',
        role: '',
        salary: '',
        mobileno: '',
        address: '',
        email: '',
        image: '',
        imagePreview: '',
        idproof: 'CITIZENSHIP',
        idImages: [],
        idProofFrontPreview: '',
        idProofBackPreview: ''
      },
      
      // Edit Form
      editForm: {
        firstName: '',
        lastName: '',
        role: '',
        salary: '',
        mobileno: '',
        address: '',
        email: '',
        image: '',
        imagePreview: '',
        idImages: [],
        idProofFrontPreview: '',
        idProofBackPreview: ''
      },
      
      // Edit ID proof upload states
      isUploadingEditIdFront: false,
      isUploadingEditIdBack: false,
      
      // Upload states
      isUploadingProfile: false,
      isUploadingIdFront: false,
      isUploadingIdBack: false,
      
      // Toast notifications
      toasts: [],
      
      // Delete confirmation
      showDeleteConfirm: false,
      staffToDelete: null
    };
  },
  computed: {
    objectId() {
      return this.$route.params.id;
    },
    filteredStaff() {
      if (!this.searchQuery.trim()) {
        return this.staffList;
      }
      const query = this.searchQuery.toLowerCase();
      return this.staffList.filter(staff => {
        const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
        const role = (staff.role || '').toLowerCase();
        const phone = (staff.mobileno || '').toLowerCase();
        const email = (staff.email || '').toLowerCase();
        return fullName.includes(query) || role.includes(query) || phone.includes(query) || email.includes(query);
      });
    },
    paginatedStaff() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredStaff.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredStaff.length / this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    hasMorePages() {
      return this.totalPages > this.visiblePages[this.visiblePages.length - 1];
    }
  },
  methods: {
    getAuthToken() {
      try {
        const token = readSessionToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        return token;
      } catch (error) {
        console.error('Error getting auth token:', error);
        this.showToast('Authentication error. Please login again.', 'error');
        throw error;
      }
    },
    async fetchStaff() {
      this.isLoading = true;
      try {
        const response = await apiService.getStaff(this.objectId);
        this.staffList = response.data?.data || [];
      } catch (error) {
        if (isNoDataError(error)) {
          this.staffList = [];
        } else if (isHmsSubscriptionInactiveError(error)) {
          // Subscription inactive — modal shown in App.vue
        } else {
          this.handleApiError(error, 'Loading staff');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPaymentList() {
      this.isPaymentLoading = true;
      try {
        const response = await apiService.getStaffSalaryPayment(this.objectId);
        this.paymentList = response.data?.data || [];
      } catch (error) {
        if (isNoDataError(error)) {
          this.paymentList = [];
        } else if (isHmsSubscriptionInactiveError(error)) {
          // Subscription inactive — modal shown in App.vue
        } else {
          this.handleApiError(error, 'Loading payment data');
        }
      } finally {
        this.isPaymentLoading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1; // Reset to first page on search
    },
    getInitials(firstName, lastName) {
      const first = (firstName || '').charAt(0).toUpperCase();
      const last = (lastName || '').charAt(0).toUpperCase();
      return first + last || '?';
    },
    formatDate(dateString) {
      if (!dateString) return '--';
      try {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (error) {
        return dateString;
      }
    },
    formatSalary(salary) {
      if (!salary) return '--';
      const num = parseFloat(salary);
      if (isNaN(num)) return salary;
      return `NPR ${num.toLocaleString('en-IN')}`;
    },
    handleImageError(event) {
      event.target.style.display = 'none';
      event.target.nextElementSibling?.classList.remove('hidden');
    },
    openAddStaffDrawer() {
      this.showAddDrawer = true;
      document.body.style.overflow = 'hidden';
    },
    closeAddStaffDrawer() {
      this.showAddDrawer = false;
      this.resetAddForm();
      document.body.style.overflow = 'auto';
    },
    resetAddForm() {
      this.addForm = {
        firstName: '',
        lastName: '',
        role: '',
        salary: '',
        mobileno: '',
        address: '',
        email: '',
        image: '',
        imagePreview: '',
        idproof: 'CITIZENSHIP',
        idImages: [],
        idProofFrontPreview: '',
        idProofBackPreview: ''
      };
      this.isUploadingProfile = false;
      this.isUploadingIdFront = false;
      this.isUploadingIdBack = false;
    },
    async handleProfileImageChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!this.validateImageFile(file)) {
        this.showToast('Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.addForm.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload image
      await this.uploadProfileImage(file, 'add');
    },
    async handleEditProfileImageChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!this.validateImageFile(file)) {
        this.showToast('Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.editForm.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload image
      await this.uploadProfileImage(file, 'edit');
    },
    async uploadProfileImage(file, formType) {
      try {
        this.isUploadingProfile = true;
        
        const formData = new FormData();
        formData.append('files', file);
        
        const uploadResponse = await apiService.uploadImage(formData);
        
        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          if (formType === 'add') {
            this.addForm.image = uploadedFilenames[0];
          } else {
            this.editForm.image = uploadedFilenames[0];
          }
          this.showToast('Profile image uploaded successfully!', 'success');
        } else {
          throw new Error('No image URL received from server');
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers
        });
        const errorMessage = error.response?.data?.message || error.message || 'Failed to upload image. Please try again.';
        this.showToast(errorMessage, 'error');
      } finally {
        this.isUploadingProfile = false;
      }
    },
    triggerIdProofFrontUpload() {
      this.$refs.idProofFrontInput?.click();
    },
    async handleIdProofFrontChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!this.validateImageFile(file)) {
        this.showToast('Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.addForm.idProofFrontPreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload image
      await this.uploadIdProofImage(file, 'front');
    },
    triggerIdProofBackUpload() {
      this.$refs.idProofBackInput?.click();
    },
    async handleIdProofBackChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!this.validateImageFile(file)) {
        this.showToast('Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.addForm.idProofBackPreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload image
      await this.uploadIdProofImage(file, 'back');
    },
    async uploadIdProofImage(file, side) {
      try {
        if (side === 'front') {
          this.isUploadingIdFront = true;
        } else {
          this.isUploadingIdBack = true;
        }
        
        const formData = new FormData();
        formData.append('files', file);
        
        const uploadResponse = await apiService.uploadImage(formData);
        
        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          const filename = uploadedFilenames[0];
          if (side === 'front') {
            // Add to idImages array at index 0
            if (this.addForm.idImages.length === 0) {
              this.addForm.idImages.push(filename);
            } else {
              this.addForm.idImages[0] = filename;
            }
          } else {
            // Add to idImages array at index 1
            if (this.addForm.idImages.length <= 1) {
              this.addForm.idImages.push(filename);
            } else {
              this.addForm.idImages[1] = filename;
            }
          }
          this.showToast('ID proof image uploaded successfully!', 'success');
        } else {
          throw new Error('No image URL received from server');
        }
      } catch (error) {
        console.error('ID proof image upload failed:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers
        });
        const errorMessage = error.response?.data?.message || error.message || 'Failed to upload image. Please try again.';
        this.showToast(errorMessage, 'error');
        if (side === 'front') {
          this.addForm.idProofFrontPreview = '';
        } else {
          this.addForm.idProofBackPreview = '';
        }
      } finally {
        if (side === 'front') {
          this.isUploadingIdFront = false;
        } else {
          this.isUploadingIdBack = false;
        }
      }
    },
    removeIdProofFront() {
      this.addForm.idProofFrontPreview = '';
      if (this.addForm.idImages.length > 0) {
        this.addForm.idImages.shift();
      }
    },
    removeIdProofBack() {
      this.addForm.idProofBackPreview = '';
      if (this.addForm.idImages.length > 1) {
        this.addForm.idImages.pop();
      } else if (this.addForm.idImages.length === 1) {
        this.addForm.idImages = [];
      }
    },
    validateImageFile(file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      return allowedTypes.includes(file.type) && file.size <= maxSize;
    },
    async createStaff() {
      // Validation
      if (!this.addForm.firstName.trim()) {
        this.showToast('Please enter first name.', 'error');
        return;
      }
      if (!this.addForm.role.trim()) {
        this.showToast('Please enter staff role.', 'error');
        return;
      }
      if (!this.addForm.mobileno.trim()) {
        this.showToast('Please enter contact number.', 'error');
        return;
      }
      if (!this.addForm.address.trim()) {
        this.showToast('Please enter address.', 'error');
        return;
      }
      if (!this.addForm.salary.trim()) {
        this.showToast('Please enter salary.', 'error');
        return;
      }
      if (!this.addForm.image) {
        this.showToast('Please upload profile picture.', 'error');
        return;
      }
      if (this.addForm.idImages.length < 2) {
        this.showToast('Please upload both ID proof images.', 'error');
        return;
      }
      
      this.isSubmitting = true;
      try {
        const payload = {
          firstName: this.addForm.firstName.trim(),
          lastName: this.addForm.lastName.trim() || '',
          role: this.addForm.role.trim(),
          salary: this.addForm.salary.trim(),
          mobileno: this.addForm.mobileno.trim(),
          address: this.addForm.address.trim(),
          email: this.addForm.email.trim() || undefined,
          image: this.addForm.image,
          idproof: this.addForm.idproof,
          idImages: this.addForm.idImages
        };
        
        // Convert payload to URLSearchParams for form-urlencoded
        const formData = new URLSearchParams();
        Object.keys(payload).forEach(key => {
          if (payload[key] !== undefined && payload[key] !== null) {
            if (Array.isArray(payload[key])) {
              payload[key].forEach(item => formData.append(key, item));
            } else {
              formData.append(key, payload[key]);
            }
          }
        });
        await apiService.registerStaff(this.objectId, formData);
        
        this.showToast('Staff added successfully!', 'success');
        this.closeAddStaffDrawer();
        await this.fetchStaff();
      } catch (error) {
        this.handleApiError(error, 'Adding staff');
      } finally {
        this.isSubmitting = false;
      }
    },
    openEditStaffDrawer(staff) {
      this.editingStaff = staff;
      
      // Extract filenames from image URLs if they're full URLs
      const extractFilename = (imageUrl) => {
        if (!imageUrl) return '';
        // If it's already just a filename, return it
        if (!imageUrl.includes('/') && !imageUrl.includes('http')) {
          return imageUrl;
        }
        // Extract filename from URL
        const parts = imageUrl.split('/');
        return parts[parts.length - 1];
      };
      
      // Load ID proof images
      const idImages = staff.idImages || [];
      const idProofFrontUrl = idImages[0] || '';
      const idProofBackUrl = idImages[1] || '';
      
      // Extract filename from profile image URL
      const profileImageUrl = staff.image || '';
      const profileImageFilename = extractFilename(profileImageUrl);
      
      this.editForm = {
        firstName: staff.firstName || '',
        lastName: staff.lastName || '',
        role: staff.role || '',
        salary: staff.salary || '',
        mobileno: staff.mobileno || '',
        address: staff.address || '',
        email: staff.email || '',
        image: profileImageFilename, // Store only filename
        imagePreview: profileImageUrl, // Keep full URL for preview
        idImages: idImages.map(extractFilename),
        idProofFrontPreview: idProofFrontUrl || '',
        idProofBackPreview: idProofBackUrl || ''
      };
      this.showEditDrawer = true;
      document.body.style.overflow = 'hidden';
    },
    closeEditStaffDrawer() {
      this.showEditDrawer = false;
      this.editingStaff = null;
      this.editForm = {
        firstName: '',
        lastName: '',
        role: '',
        salary: '',
        mobileno: '',
        address: '',
        email: '',
        image: '',
        imagePreview: '',
        idImages: [],
        idProofFrontPreview: '',
        idProofBackPreview: ''
      };
      this.isUploadingEditIdFront = false;
      this.isUploadingEditIdBack = false;
      document.body.style.overflow = 'auto';
    },
    triggerEditIdProofFrontUpload() {
      this.$refs.editIdProofFrontInput?.click();
    },
    async handleEditIdProofFrontChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!this.validateImageFile(file)) {
        this.showToast('Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.editForm.idProofFrontPreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload image
      await this.uploadEditIdProofImage(file, 'front');
    },
    triggerEditIdProofBackUpload() {
      this.$refs.editIdProofBackInput?.click();
    },
    async handleEditIdProofBackChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!this.validateImageFile(file)) {
        this.showToast('Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.editForm.idProofBackPreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload image
      await this.uploadEditIdProofImage(file, 'back');
    },
    async uploadEditIdProofImage(file, side) {
      try {
        if (side === 'front') {
          this.isUploadingEditIdFront = true;
        } else {
          this.isUploadingEditIdBack = true;
        }
        
        const formData = new FormData();
        formData.append('files', file);
        
        const uploadResponse = await apiService.uploadImage(formData);
        
        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          const filename = uploadedFilenames[0];
          if (side === 'front') {
            // Add to idImages array at index 0
            if (this.editForm.idImages.length === 0) {
              this.editForm.idImages.push(filename);
            } else {
              this.editForm.idImages[0] = filename;
            }
          } else {
            // Add to idImages array at index 1
            if (this.editForm.idImages.length <= 1) {
              this.editForm.idImages.push(filename);
            } else {
              this.editForm.idImages[1] = filename;
            }
          }
          this.showToast('ID proof image uploaded successfully!', 'success');
        } else {
          throw new Error('No image URL received from server');
        }
      } catch (error) {
        console.error('ID proof image upload failed:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers
        });
        const errorMessage = error.response?.data?.message || error.message || 'Failed to upload image. Please try again.';
        this.showToast(errorMessage, 'error');
        if (side === 'front') {
          this.editForm.idProofFrontPreview = '';
        } else {
          this.editForm.idProofBackPreview = '';
        }
      } finally {
        if (side === 'front') {
          this.isUploadingEditIdFront = false;
        } else {
          this.isUploadingEditIdBack = false;
        }
      }
    },
    removeEditIdProofFront() {
      this.editForm.idProofFrontPreview = '';
      if (this.editForm.idImages.length > 0) {
        this.editForm.idImages.shift();
      }
    },
    removeEditIdProofBack() {
      this.editForm.idProofBackPreview = '';
      if (this.editForm.idImages.length > 1) {
        this.editForm.idImages.pop();
      } else if (this.editForm.idImages.length === 1) {
        this.editForm.idImages = [];
      }
    },
    async updateStaff() {
      if (!this.editingStaff) return;
      
      // Validation
      if (!this.editForm.firstName.trim()) {
        this.showToast('Please enter first name.', 'error');
        return;
      }
      if (!this.editForm.role.trim()) {
        this.showToast('Please enter staff role.', 'error');
        return;
      }
      if (!this.editForm.mobileno.trim()) {
        this.showToast('Please enter contact number.', 'error');
        return;
      }
      if (!this.editForm.address.trim()) {
        this.showToast('Please enter address.', 'error');
        return;
      }
      if (!this.editForm.salary.trim()) {
        this.showToast('Please enter salary.', 'error');
        return;
      }
      
      this.isSubmitting = true;
      try {
        const payload = {
          firstName: this.editForm.firstName.trim(),
          lastName: this.editForm.lastName.trim() || undefined,
          role: this.editForm.role.trim() || undefined,
          salary: this.editForm.salary.trim() || undefined,
          mobileno: this.editForm.mobileno.trim() || undefined,
          address: this.editForm.address.trim() || undefined,
          email: this.editForm.email.trim() || undefined,
          image: this.editForm.image || undefined,
          idImages: this.editForm.idImages.length > 0 ? this.editForm.idImages : undefined
        };
        
        // Remove undefined fields
        Object.keys(payload).forEach(key => {
          if (payload[key] === undefined) {
            delete payload[key];
          }
        });
        
        await apiService.updateStaffMember(this.editingStaff.staffId, this.objectId, payload);
        
        this.showToast('Staff updated successfully!', 'success');
        this.closeEditStaffDrawer();
        await this.fetchStaff();
      } catch (error) {
        this.handleApiError(error, 'Updating staff');
      } finally {
        this.isSubmitting = false;
      }
    },
    confirmDeleteStaff(staff) {
      if (!staff) return;
      this.staffToDelete = staff;
      this.showDeleteConfirm = true;
      document.body.style.overflow = 'hidden';
    },
    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.staffToDelete = null;
      document.body.style.overflow = 'auto';
    },
    async deleteStaff() {
      if (!this.staffToDelete) return;
      
      this.isDeleting = true;
      try {
        await apiService.deleteStaffMember(this.staffToDelete.staffId, this.objectId);
        
        this.showToast('Staff deleted successfully!', 'success');
        this.closeDeleteConfirm();
        await this.fetchStaff();
      } catch (error) {
        this.handleApiError(error, 'Deleting staff');
      } finally {
        this.isDeleting = false;
      }
    },
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random(); // Ensure unique ID
      this.toasts.push({ id, message, type });
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },
    // Enhanced API error handling
    handleApiError(error, context = 'Operation') {
      // Skip error handling for "no data" cases (status 400 with errorcode 2003)
      if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
        return;
      }

      console.error(`${context} error:`, error);
      
      let message = null;
      let type = 'error';

      if (error?.response?.data) {
        const errorData = error.response.data;
        const status = error.response.status;
        
        // For HTTP 400 errors, always use the reason field from API response
        if (status === 400) {
          if (errorData.reason) {
            message = errorData.reason;
          }
          // If no reason provided for 400 error, don't show toast (no generic messages)
        } else {
          // For other status codes, prioritize reason field
          if (errorData.reason) {
            message = errorData.reason;
          } else if (errorData.message) {
            message = errorData.message;
          } else {
            // Fallback messages for other status codes only if no reason/message
            switch (status) {
              case 401:
                message = 'Session expired. Please login again.';
                setTimeout(() => {
                  this.$router.push('/login');
                }, 2000);
                break;
              case 403:
                message = 'You do not have permission to perform this action.';
                break;
              case 404:
                message = 'The requested resource was not found.';
                break;
              case 429:
                message = 'Too many requests. Please wait a moment and try again.';
                break;
              case 500:
                message = 'Server error occurred. Please try again later.';
                break;
              case 503:
                message = 'Service temporarily unavailable. Please try again later.';
                break;
            }
          }
        }
      } else if (error?.request) {
        message = 'No response from server. Please check your internet connection.';
      } else if (error?.message) {
        message = error.message;
      }

      // Only show toast if we have a message (genuine errors only)
      if (message) {
        this.showToast(message, type);
      }
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'payment') {
        this.fetchPaymentList();
      }
    }
  },
  mounted() {
    this.fetchStaff();
  }
};
</script>

<style scoped>
.staff-management-container {
  padding: 0;
  background-color: #f8fafc;
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header Section */
.header-section {
  background: white;
  border-radius: 0;
  padding: 16px 24px;
  margin-bottom: 0;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.025em;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
  padding: 8px 16px;
  font-size: 14px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
  background: linear-gradient(135deg, #4c1d75, #6b21d4);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Tabs Section */
.tabs-section {
  background: white;
  border-radius: 0;
  padding: 0 24px;
  margin-bottom: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.tabs {
  display: flex;
  gap: 32px;
  border-bottom: 2px solid #e2e8f0;
}

.tab-button {
  padding: 16px 0;
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: color 0.2s ease;
}

.tab-button:hover {
  color: #5b2e8e;
}

.tab-button.active {
  color: #5b2e8e;
  font-weight: 600;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #5b2e8e;
}

/* Search Section */
.search-section {
  background: white;
  border-radius: 0;
  padding: 16px 24px;
  margin-bottom: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  max-width: 400px;
}

.search-box svg {
  color: #64748b;
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-size: 14px;
  color: #1e293b;
}

.search-box input::placeholder {
  color: #94a3b8;
}

/* Table Section */
.table-section {
  background: white;
  border-radius: 0;
  box-shadow: none;
  border: none;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-container {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
}

.loading-content {
  text-align: center;
  color: #5b2e8e;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #5b2e8e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  display: block;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  padding: 80px 24px;
  text-align: center;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-illustration {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.empty-illustration-img {
  width: 250px;
  height: 250px;
  max-width: 100%;
  object-fit: contain;
}

.empty-text {
  font-size: 18px;
  color: #64748b;
  margin: 0;
}

.table-wrapper {
  overflow-x: auto;
}

.staff-table {
  width: 100%;
  border-collapse: collapse;
}

.staff-table thead {
  background: #f8fafc;
}

.staff-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  vertical-align: middle;
}

.staff-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #1e293b;
  white-space: nowrap;
  vertical-align: middle;
}

.table-row:hover {
  background: #f8fafc;
}

.staff-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.staff-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.staff-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #5b2e8e;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.staff-name {
  color: #5b2e8e;
  font-weight: 500;
}

.action-menu {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.action-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: #5b2e8e;
  color: white;
}

.action-button-delete:hover {
  background: #ef4444;
  color: white;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
}

.pagination-button {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}

.pagination-button:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #5b2e8e;
  color: #5b2e8e;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-number {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  font-size: 14px;
  transition: all 0.2s ease;
}

.pagination-number:hover {
  background: #f8fafc;
  border-color: #5b2e8e;
  color: #5b2e8e;
}

.pagination-number.active {
  background: #5b2e8e;
  border-color: #5b2e8e;
  color: white;
}

.pagination-ellipsis {
  padding: 0 8px;
  color: #64748b;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  overflow-y: auto;
}

/* Drawer Styles (for Edit Staff) */
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.drawer {
  width: 100%;
  max-width: 480px;
  background: white;
  height: 100%;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
  display: flex;
  flex-direction: column;
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.drawer-header {
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #5b2e8e;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.drawer-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.drawer-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.drawer-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.drawer-content {
  flex: 1;
  padding: 24px;
}

.drawer-footer {
  display: flex;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  margin-top: 24px;
}

.drawer-footer .btn {
  flex: 1;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.create-modal {
  max-width: 600px;
  overflow-x: hidden;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  position: relative;
  overflow: hidden;
  color: white;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  opacity: 0.95;
}

.header-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E") repeat;
}

.modal-header-content {
  position: relative;
  z-index: 2;
  padding: 42px 80px 42px 42px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
}

.modal-header h2 {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 40px;
  line-height: 1.2;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 3;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  color: white;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.close-btn svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 32px;
  padding-bottom: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  position: relative;
  max-width: 100%;
  box-sizing: border-box;
}

.profile-picture-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.profile-picture-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 16px;
  background: #f1f5f9;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.change-photo-link {
  color: #5b2e8e;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 12px;
  transition: color 0.2s ease;
}

.change-photo-link:hover {
  color: #4a2575;
}

.upload-progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
  animation: fadeInOverlay 0.3s ease-out;
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.modern-spinner {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  width: 56px;
  height: 56px;
  border-top-color: #ffffff;
  border-right-color: rgba(255, 255, 255, 0.3);
  animation-duration: 1.2s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
}

.spinner-ring:nth-child(2) {
  width: 44px;
  height: 44px;
  border-top-color: #a78bfa;
  border-right-color: rgba(167, 139, 250, 0.3);
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  animation-direction: reverse;
  top: 6px;
  left: 6px;
}

.spinner-ring:nth-child(3) {
  width: 32px;
  height: 32px;
  border-top-color: #7c3aed;
  border-right-color: rgba(124, 58, 237, 0.3);
  animation-duration: 0.8s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  top: 12px;
  left: 12px;
}

@keyframes spin-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeInOverlay {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.uploading-text {
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  animation: pulse-text 2s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  display: block;
}

.form-input {
  width: 100%;
  max-width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: white;
  color: #1e293b;
  outline: none;
  transition: all 0.2s ease;
  font-family: inherit;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 4px rgba(91, 46, 142, 0.1);
}

.form-input::placeholder {
  color: #94a3b8;
}

.upload-box {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.upload-box:hover {
  border-color: #5b2e8e;
  background: #f1f5f9;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #64748b;
}

.upload-placeholder svg {
  color: #94a3b8;
}

.upload-placeholder span {
  font-size: 16px;
  font-weight: 500;
  color: #475569;
}

.upload-preview {
  position: relative;
  width: 100%;
  height: 100%;
}

.upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.remove-upload {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.remove-upload:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.create-staff-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.form-actions {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 24px 32px;
  background: white;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 10;
  flex-shrink: 0;
  margin: 0;
}

.payment-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.payment-content .table-section {
  margin-top: 0;
}

.payment-content .table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.payment-content .staff-table {
  min-width: 100%;
  table-layout: auto;
}

.payment-content .staff-table th,
.payment-content .staff-table td {
  white-space: nowrap;
  text-align: left;
  vertical-align: middle;
}

.payment-content .staff-table th {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f8fafc;
}

.net-amount {
  font-weight: 700;
  color: #10b981;
  text-align: right;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background: #d1fae5;
  color: #065f46;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

/* Responsive */
@media (max-width: 768px) {
  .staff-management-container {
    padding: 16px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .title-icon {
    width: 40px;
    height: 40px;
  }

  .title-icon svg {
    width: 20px;
    height: 20px;
  }

  .page-title {
    gap: 12px;
  }

  .page-title h1 {
    font-size: 18px;
  }
  
  .drawer {
    max-width: 100%;
  }
  
  .table-container {
    min-height: 300px;
  }
  
  .empty-state {
    min-height: 300px;
    padding: 60px 16px;
  }
  
  .empty-illustration-img {
    width: 180px;
    height: 180px;
  }
  
  .staff-table {
    font-size: 12px;
  }
  
  .staff-table th,
  .staff-table td {
    padding: 12px 8px;
  }
}

/* Toast Notification Styles */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-left: 4px solid transparent;
  min-width: 320px;
  pointer-events: auto;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  color: #6b7280;
  background: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
  .toast-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
    padding: 14px 16px;
  }

  .toast-message {
    font-size: 14px;
  }
}

/* Delete Confirmation Modal */
.delete-confirm-modal {
  max-width: 480px;
}

.delete-confirm-content {
  text-align: center;
  padding: 24px 0;
}

.delete-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  border-radius: 50%;
  color: #ef4444;
}

.delete-icon-wrapper svg {
  width: 48px;
  height: 48px;
}

.delete-confirm-content h3 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.delete-staff-name {
  font-size: 18px;
  font-weight: 600;
  color: #5b2e8e;
  margin: 16px 0;
}

.delete-warning {
  font-size: 14px;
  color: #64748b;
  margin: 16px 0 0 0;
  line-height: 1.6;
}
</style>

